import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { ScrollArea } from './ui/scroll-area';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from './ui/dialog';
import {
    Bell,
    BellRing,
    Check,
    CheckCircle,
    Clock,
    AlertTriangle,
    Table2,
    User,
    X,
    RefreshCw
} from 'lucide-react';
import { cn } from '../lib/utils';

// Priority colors
const PRIORITY_COLORS = {
    urgent: 'bg-red-500 text-white',
    high: 'bg-orange-500 text-white',
    normal: 'bg-blue-500 text-white',
    low: 'bg-gray-400 text-white',
};

const PRIORITY_BADGE = {
    urgent: 'bg-red-100 text-red-700 border-red-300',
    high: 'bg-orange-100 text-orange-700 border-orange-300',
    normal: 'bg-blue-100 text-blue-700 border-blue-300',
    low: 'bg-gray-100 text-gray-700 border-gray-300',
};

// Notification Card Component
const NotificationCard = ({ notification, onAcknowledge, onResolve }) => {
    const createdAt = new Date(notification.created_at);
    const timeAgo = getTimeAgo(createdAt);
    
    return (
        <div className={cn(
            "p-3 rounded-lg border-l-4 bg-white shadow-sm",
            notification.status === 'pending' && "border-l-orange-500 animate-pulse",
            notification.status === 'acknowledged' && "border-l-blue-500",
            notification.status === 'resolved' && "border-l-green-500 opacity-60",
            notification.priority === 'urgent' && "border-l-red-500",
        )}>
            <div className="flex justify-between items-start gap-2">
                <div className="flex-1">
                    <div className="flex items-center gap-2">
                        <Table2 className="w-4 h-4 text-gray-500" />
                        <span className="font-semibold text-sm">
                            Table {notification.table_number}
                        </span>
                        <Badge variant="outline" className={cn("text-[10px]", PRIORITY_BADGE[notification.priority])}>
                            {notification.priority}
                        </Badge>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">{notification.message}</p>
                    <div className="flex items-center gap-3 mt-2 text-[10px] text-gray-400">
                        <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {notification.created_by_name}
                        </span>
                        <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {timeAgo}
                        </span>
                    </div>
                </div>
                
                <div className="flex flex-col gap-1">
                    {notification.status === 'pending' && (
                        <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={() => onAcknowledge(notification.id)}
                        >
                            <Check className="w-3 h-3 mr-1" />
                            OK
                        </Button>
                    )}
                    {notification.status !== 'resolved' && (
                        <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 text-xs text-green-600"
                            onClick={() => onResolve(notification.id)}
                        >
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Résolu
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

// Helper to format time ago
function getTimeAgo(date) {
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    
    if (diff < 60) return "À l'instant";
    if (diff < 3600) return `Il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `Il y a ${Math.floor(diff / 3600)}h`;
    return date.toLocaleDateString('fr-FR');
}

// Service Notifications Button Component (for nav/header)
export const ServiceNotificationsButton = ({ className }) => {
    const { api, hasPermission, user } = useAuth();
    const [count, setCount] = useState(0);
    const [showDialog, setShowDialog] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // Check if user can see notifications
    const canView = hasPermission?.('manage_staff') || ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT'].includes(user?.role);
    
    // Fetch pending count
    const fetchCount = useCallback(async () => {
        if (!canView) return;
        try {
            const res = await api().get('/service-notifications/pending-count');
            setCount(res.data?.count || 0);
        } catch (error) {
            console.error('Error fetching notification count:', error);
        }
    }, [api, canView]);
    
    // Fetch notifications
    const fetchNotifications = useCallback(async () => {
        if (!canView) return;
        setLoading(true);
        try {
            const res = await api().get('/service-notifications?limit=20');
            setNotifications(res.data || []);
        } catch (error) {
            console.error('Error fetching notifications:', error);
            toast.error('Erreur lors du chargement des notifications');
        } finally {
            setLoading(false);
        }
    }, [api, canView]);
    
    // Acknowledge notification
    const acknowledgeNotification = async (id) => {
        try {
            await api().post(`/service-notifications/${id}/acknowledge`);
            toast.success('Notification acquittée');
            fetchNotifications();
            fetchCount();
        } catch (error) {
            toast.error('Erreur');
        }
    };
    
    // Resolve notification
    const resolveNotification = async (id) => {
        try {
            await api().post(`/service-notifications/${id}/resolve`);
            toast.success('Notification résolue');
            fetchNotifications();
            fetchCount();
        } catch (error) {
            toast.error('Erreur');
        }
    };
    
    // Initial fetch and polling
    useEffect(() => {
        fetchCount();
        const interval = setInterval(fetchCount, 15000); // Poll every 15s
        return () => clearInterval(interval);
    }, [fetchCount]);
    
    // Fetch notifications when dialog opens
    useEffect(() => {
        if (showDialog) {
            fetchNotifications();
        }
    }, [showDialog, fetchNotifications]);
    
    if (!canView) return null;
    
    return (
        <>
            <Button
                variant="ghost"
                size="sm"
                className={cn("relative", className)}
                onClick={() => setShowDialog(true)}
                data-testid="service-notifications-btn"
            >
                {count > 0 ? (
                    <BellRing className="w-5 h-5 text-orange-500 animate-bounce" />
                ) : (
                    <Bell className="w-5 h-5 text-gray-500" />
                )}
                {count > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] rounded-full w-5 h-5 flex items-center justify-center font-bold">
                        {count > 9 ? '9+' : count}
                    </span>
                )}
            </Button>
            
            <Dialog open={showDialog} onOpenChange={setShowDialog}>
                <DialogContent className="max-w-md max-h-[80vh]">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <BellRing className="w-5 h-5 text-orange-500" />
                            Notifications de Service
                            {count > 0 && (
                                <Badge className="bg-red-500">{count} en attente</Badge>
                            )}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="flex justify-end">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={fetchNotifications}
                            disabled={loading}
                        >
                            <RefreshCw className={cn("w-4 h-4 mr-1", loading && "animate-spin")} />
                            Actualiser
                        </Button>
                    </div>
                    
                    <ScrollArea className="h-[400px] pr-2">
                        <div className="space-y-2">
                            {notifications.length === 0 ? (
                                <div className="text-center py-8 text-gray-400">
                                    <Bell className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                    <p>Aucune notification</p>
                                </div>
                            ) : (
                                notifications.map(notif => (
                                    <NotificationCard
                                        key={notif.id}
                                        notification={notif}
                                        onAcknowledge={acknowledgeNotification}
                                        onResolve={resolveNotification}
                                    />
                                ))
                            )}
                        </div>
                    </ScrollArea>
                </DialogContent>
            </Dialog>
        </>
    );
};

// Service Notifications Panel Component (for dashboard embedding)
const ServiceNotificationsPanel = ({ maxHeight = '300px' }) => {
    const { api, hasPermission, user } = useAuth();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    
    const canView = hasPermission?.('manage_staff') || ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT'].includes(user?.role);
    
    const fetchNotifications = useCallback(async () => {
        if (!canView) return;
        try {
            const res = await api().get('/service-notifications?status=pending&limit=10');
            setNotifications(res.data || []);
        } catch (error) {
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    }, [api, canView]);
    
    const acknowledgeNotification = async (id) => {
        try {
            await api().post(`/service-notifications/${id}/acknowledge`);
            fetchNotifications();
        } catch (error) {
            toast.error('Erreur');
        }
    };
    
    const resolveNotification = async (id) => {
        try {
            await api().post(`/service-notifications/${id}/resolve`);
            fetchNotifications();
        } catch (error) {
            toast.error('Erreur');
        }
    };
    
    useEffect(() => {
        fetchNotifications();
        const interval = setInterval(fetchNotifications, 10000);
        return () => clearInterval(interval);
    }, [fetchNotifications]);
    
    if (!canView) return null;
    
    return (
        <Card>
            <CardHeader className="py-3">
                <CardTitle className="text-sm flex items-center gap-2">
                    <BellRing className={cn(
                        "w-4 h-4",
                        notifications.length > 0 ? "text-orange-500 animate-pulse" : "text-gray-400"
                    )} />
                    Appels Service
                    {notifications.length > 0 && (
                        <Badge variant="destructive" className="text-[10px]">
                            {notifications.length}
                        </Badge>
                    )}
                </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
                <ScrollArea style={{ maxHeight }}>
                    <div className="space-y-2">
                        {loading ? (
                            <div className="flex justify-center py-4">
                                <RefreshCw className="w-5 h-5 animate-spin text-gray-400" />
                            </div>
                        ) : notifications.length === 0 ? (
                            <p className="text-center text-xs text-gray-400 py-4">
                                Aucun appel en attente
                            </p>
                        ) : (
                            notifications.map(notif => (
                                <NotificationCard
                                    key={notif.id}
                                    notification={notif}
                                    onAcknowledge={acknowledgeNotification}
                                    onResolve={resolveNotification}
                                />
                            ))
                        )}
                    </div>
                </ScrollArea>
            </CardContent>
        </Card>
    );
};

export default ServiceNotificationsPanel;
